// web/src/player/HostCharacter.tsx
// Create-screen hero character. Unlike RoastCharacter (the player's full-frame
// SM_Main lip-sync rig), this is a decorative, data-bound display: it loads the
// "host" artboard from mr_roast.riv (driven by SM_Main) and switches between Mr
// Roast and Mr Toast by setting the "Character" enum PROPERTY on the auto-bound
// view model. No remount on mode change — just update the enum, so the swap is
// instant (the state machine cross-fades the nested character).
import { useRive, Layout, Fit, Alignment } from "@rive-app/react-webgl2";
import { useEffect } from "react";
import type { JSX } from "react";
import type { Mode } from "@/types/roast";

/** Values of the "Character" enum property on the host artboard's view model. */
const CHARACTER_VALUE: Record<Mode, string> = { roast: "MrRoast", toast: "MrToast" };

export function HostCharacter({ src, mode }: { src: string; mode: Mode }): JSX.Element {
  const { rive, RiveComponent } = useRive({
    src,
    artboard: "host",
    stateMachines: "SM_Main",
    autoBind: true,
    autoplay: true,
    layout: new Layout({ fit: Fit.Contain, alignment: Alignment.Center }),
  });

  // Drive the Character enum from the current mode (no remount).
  useEffect(() => {
    const vmi = rive?.viewModelInstance;
    if (!vmi) return;
    try {
      const ch = vmi.enum("Character");
      if (ch) ch.value = CHARACTER_VALUE[mode];
    } catch {
      /* enum missing on this build of the .riv — leave the default character */
    }
  }, [rive, mode]);

  // The character box is a fixed size (same at every breakpoint), so it never
  // resizes with the viewport — no custom resize handling needed, and no
  // per-frame WebGL realloc that would flicker.

  return <RiveComponent style={{ width: "100%", height: "100%" }} />;
}
